<template>
    <div class="content-wrapper">
        <div class="content">
            <template v-if="timezoneConfirmed">
                <div class="crm-stage">
                    <div class="panel pad">
                        <div class="section-title">
                            <div>
                                <span class="eyebrow">CRM-система</span>
                                <h2>Выберите CRM, из которой будем получать данные</h2>
                                <p>
                                    После выбора покажем точные шаги: где взять доступы, какие поля
                                    заполнить и когда можно продолжить подключение.
                                </p>
                            </div>
                            <span class="badge">Шаг 3</span>
                        </div>

                        <div class="crm-grid">
                            <ConnectorSettingsCard
                                v-for="item in crmItems"
                                :key="item.type"
                                :connector="item.connector"
                                :tag="item.tag"
                                :active="selectedCrmType === item.type"
                                @select="selectCrm(item)"
                            />
                            <OtherConnectorCard
                                tag="API"
                                :active="selectedCrmType === 'other'"
                                @select="selectCrm({ type: 'other', name: 'Другое', tag: 'API' })"
                            />
                        </div>

                        <div class="crm-toolbar">
                            <div class="selected-pill">
                                Выбрана CRM:
                                <b>{{ selectedCrmName }}</b>
                            </div>
                            <button class="outline-btn" type="button" @click="resetCrmSelection">
                                Сбросить выбор
                            </button>
                        </div>
                    </div>
                </div>
            </template>

            <div v-else class="timezone-stage">
                <div class="timezone-card">
                    <span class="eyebrow">Часовой пояс</span>
                    <h2>Выберите часовой пояс проекта</h2>
                    <p>
                        Он будет использоваться для расписания уведомлений, отчётов и времени
                        обработки данных.
                    </p>
                    <div class="form-line">
                        <div class="field-label">Часовой пояс</div>
                        <TimeZonePicker
                            class="timezone-picker"
                            :value="pickerValue"
                            :recommendCurrentTimezone="true"
                            append-icon="mdi-chevron-down"
                            label=""
                            :persistent-label="false"
                            hide-details
                            @input="onPickerInput"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useProject } from '@/hooks/useProject'
import ConnectorSettingsCard from '@/components/project/onBoarding/ConnectorSettingsCard.vue'
import { getCurrentInstance, computed, ref, onMounted, onBeforeUnmount, watch, inject } from 'vue'
import OtherConnectorCard from './OtherConnectorCard.vue'
import TimeZonePicker from '../../ui/TimeZonePicker.vue'
import api from '@/api/api'
import { getAvailableCrmTypes } from '@/helpers/onboardingCrmList'
import moment from 'moment-timezone'

const MOSCOW_TZ = 'Europe/Moscow'
const DEFAULT_CRM_TYPE = 'yClients'

/** Теги как в макете dh_other_crm_onboarding */
const CRM_TAG_BY_TYPE = {
    cloud1C: 'API',
    profSalonBat: 'Логин',
    universeSoft: 'API',
    dentalPro: 'Логин',
    archimed: 'API',
    sycret: 'API',
    medesk: 'API',
    altegio: 'Инструкция',
    arnicaV2: 'Логин',
    dikidi: 'Инструкция',
    klientix: 'API',
    waitThai: 'API',
    yClients: 'Маркет',
    drsoft: 'API',
    other: 'API',
}

function resolveDefaultTimezone() {
    const allowed = [
        ...moment.tz.zonesForCountry('RU'),
        ...moment.tz.zonesForCountry('UA'),
        ...moment.tz.zonesForCountry('BY'),
        ...moment.tz.zonesForCountry('KZ'),
    ]
    const guessed = moment.tz.guess(true)
    if (allowed.includes(guessed)) {
        return guessed
    }
    return MOSCOW_TZ
}

export default {
    name: 'SelectCRMSystem',

    components: {
        TimeZonePicker,
        ConnectorSettingsCard,
        OtherConnectorCard,
    },

    setup(props, { emit }) {
        const { project } = useProject()
        const root = getCurrentInstance().proxy
        const store = root.$store
        const wizardNav = inject('onboardingWizardNav', null)
        const connectorsList = store.getters.connectorsHashMapWithKeysByConnectorsType
        const pickerValue = ref(null)
        const timezoneConfirmed = ref(false)
        const selectedCrmType = ref(null)

        const crmSystems = [
            'cloud1C',
            'profSalonBat',
            'universeSoft',
            'dentalPro',
            'archimed',
            'sycret',
            'medesk',
            'altegio',
            'arnicaV2',
            'dikidi',
            'klientix',
            'waitThai',
            'yClients',
            'drsoft',
        ]

        // getter connectorsHashMapWithKeysByConnectorsType не учитывает showCondition (WaitThai только вне PRODUCTION)
        const crmItems = computed(() =>
            getAvailableCrmTypes(crmSystems, connectorsList, project.value)
                .map((type) => {
                    const connector = connectorsList[type]
                    const avatar = store.getters.connectorAvatarByType(type)
                    return {
                        type,
                        connector,
                        tag: CRM_TAG_BY_TYPE[type] || 'API',
                        name: avatar?.name || connector?.name || type,
                    }
                }),
        )

        const selectedCrmName = computed(() => {
            if (!selectedCrmType.value) return '—'
            if (selectedCrmType.value === 'other') return 'Другое'
            const item = crmItems.value.find((x) => x.type === selectedCrmType.value)
            return item?.name || selectedCrmType.value
        })

        const selectedCrmPayload = computed(() => {
            if (!selectedCrmType.value) return null
            if (selectedCrmType.value === 'other') {
                return { name: 'Другое', type: 'other' }
            }
            const item = crmItems.value.find((x) => x.type === selectedCrmType.value)
            if (!item) return null
            return item.connector
        })

        const persistTimezone = (timezone) => {
            sessionStorage.setItem('projectTimeZone', timezone)
            emit('timezone-changed', timezone)
            try {
                const projectId = String(project.value?.id || '')
                if (projectId && timezone) {
                    api.networkFixer.updateProjectTimezone({ projectId, timezone: String(timezone) })
                }
            } catch (e) {
                // ignore
            }
        }

        const onPickerInput = (timezone) => {
            pickerValue.value = timezone
        }

        const ensureDefaultCrmSelection = () => {
            if (selectedCrmType.value) return
            const hasDefault = crmItems.value.some((item) => item.type === DEFAULT_CRM_TYPE)
            selectedCrmType.value = hasDefault ? DEFAULT_CRM_TYPE : crmItems.value[0]?.type || 'other'
        }

        const confirmTimezone = () => {
            if (!pickerValue.value) return
            persistTimezone(pickerValue.value)
            timezoneConfirmed.value = true
            ensureDefaultCrmSelection()
        }

        const backToTimezone = () => {
            timezoneConfirmed.value = false
            emit('timezone-changed', null)
        }

        const selectCrm = (item) => {
            selectedCrmType.value = item?.type || null
        }

        const resetCrmSelection = () => {
            const hasDefault = crmItems.value.some((item) => item.type === DEFAULT_CRM_TYPE)
            selectedCrmType.value = hasDefault ? DEFAULT_CRM_TYPE : null
        }

        const confirmCrmSelection = () => {
            const connector = selectedCrmPayload.value
            if (!connector?.type || !project.value?.id) return
            window.localStorage.setItem(
                project.value.id + '-onboardingSelectedCrm',
                JSON.stringify(connector),
            )
            store.commit('project/updateCurrentAddingConnector', connector.type)
            emit('refreshStep')
        }

        const syncWizardNav = () => {
            if (!wizardNav) return
            if (!timezoneConfirmed.value) {
                wizardNav.set({
                    canBack: false,
                    canNext: Boolean(pickerValue.value),
                    nextLoading: false,
                    nextLabel: 'Далее',
                    onNext: confirmTimezone,
                    onBack: null,
                })
                return
            }
            wizardNav.set({
                canBack: true,
                canNext: Boolean(selectedCrmType.value),
                nextLoading: false,
                nextLabel: 'Далее',
                onNext: confirmCrmSelection,
                onBack: backToTimezone,
            })
        }

        watch([pickerValue, timezoneConfirmed, selectedCrmType], syncWizardNav, { immediate: true })

        onMounted(() => {
            const savedSession = sessionStorage.getItem('projectTimeZone')
            if (savedSession) {
                pickerValue.value = savedSession
                timezoneConfirmed.value = true
                emit('timezone-changed', savedSession)
                ensureDefaultCrmSelection()
                return
            }
            // Prefill from project if present, but still show the timezone step until user confirms
            pickerValue.value = project.value?.timezone || resolveDefaultTimezone()
            timezoneConfirmed.value = false
        })

        onBeforeUnmount(() => {
            wizardNav?.reset?.()
        })

        return {
            pickerValue,
            timezoneConfirmed,
            onPickerInput,
            crmItems,
            selectedCrmType,
            selectedCrmName,
            selectCrm,
            resetCrmSelection,
        }
    },
}
</script>
<style lang="sass" scoped>
@import '@/vars.sass'

// Как в макете / CreateProjectStep — нет в vars.sass
$ob-input-border: #D9DEEA
$ob-crm-border: #DDE3EF

.content
    align-items: stretch
    justify-content: flex-start
    display: flex
    flex-direction: column
    width: 100%

.crm-stage
    width: 100%
    display: flex
    align-items: flex-start
    justify-content: center

.panel
    width: 100%
    border: 1px solid $border-new
    border-radius: 22px
    background: #fff
    box-shadow: 0 12px 32px rgba(26, 26, 46, 0.06)
    &.pad
        padding: 24px

.section-title
    display: flex
    align-items: flex-start
    justify-content: space-between
    gap: 16px
    margin-bottom: 20px

    h2
        font-size: 22px
        letter-spacing: -0.04em
        line-height: 1.1
        margin: 0
        font-weight: 800
        color: $dark-new

    p
        margin: 8px 0 0
        color: $muted-new
        font-size: 13px
        max-width: 640px
        line-height: 1.45

.badge
    display: inline-flex
    align-items: center
    gap: 6px
    height: 28px
    border-radius: 999px
    padding: 0 10px
    background: $bcg-card-new
    color: $main-new
    font-size: 11px
    font-weight: 800
    white-space: nowrap
    flex: 0 0 auto

.crm-grid
    display: grid
    grid-template-columns: repeat(4, minmax(0, 1fr))
    gap: 14px

    > *
        min-width: 0
        max-width: 100%

.crm-toolbar
    display: flex
    align-items: center
    justify-content: space-between
    gap: 16px
    margin-top: 18px

.selected-pill
    display: inline-flex
    align-items: center
    gap: 8px
    border: 1px solid $border-new
    border-radius: 999px
    background: #F8FAFF
    padding: 8px 12px
    color: #596174
    font-size: 12px
    b
        color: $dark-new

.outline-btn
    height: 38px
    border: 1.5px solid rgba(82, 90, 213, 0.38)
    border-radius: 10px
    background: #fff
    color: $main-new
    font-size: 12px
    font-weight: 900
    padding: 0 14px
    cursor: pointer
    font-family: inherit
    white-space: nowrap
    &:hover
        border-color: $main-new

.timezone-stage
    width: 100%
    min-height: 100%
    display: flex
    align-items: center
    justify-content: center

.timezone-card
    width: min(620px, 100%)
    border: 1px solid $border-new
    border-radius: 26px
    background: #fff
    padding: 32px
    box-shadow: 0 16px 42px rgba(26, 26, 46, 0.08)

    h2
        font-size: 32px
        line-height: 1.05
        letter-spacing: -0.055em
        margin: 0 0 12px
        font-weight: 800
        color: $dark-new

    p
        margin: 0 0 24px
        color: $muted-new
        font-size: 14px
        line-height: 1.45

.eyebrow
    display: inline-flex
    align-items: center
    gap: 6px
    margin-bottom: 10px
    color: $main-new
    font-size: 11px
    font-weight: 900
    text-transform: uppercase
    letter-spacing: 0.14em

.form-line
    display: grid
    gap: 0
    max-width: 340px

.field-label
    font-size: 12px
    font-weight: 800
    color: #394155
    margin-bottom: 7px

.timezone-picker
    width: 100%
    max-width: 340px

    ::v-deep .base-input-outlined-wrapper
        width: 100%

    ::v-deep .v-input
        margin-top: 0
        padding-top: 0
        font-size: 14px

    ::v-deep .v-input__control
        min-height: 44px

    ::v-deep .v-input__slot
        margin-bottom: 0 !important
        min-height: 44px !important
        padding: 0 4px 0 13px !important
        background: #fff !important

    ::v-deep fieldset
        border: 1px solid $ob-input-border !important
        border-radius: 11px !important
        legend
            display: none

    ::v-deep .v-text-field--outlined.v-input--is-focused fieldset
        border-color: $main-new !important
        box-shadow: 0 0 0 4px rgba(82, 90, 213, 0.10)

    ::v-deep input
        color: $dark-new !important
        max-height: 44px
        padding: 0 !important
        caret-color: $dark-new

    ::v-deep .v-input__append-inner
        margin-top: 0 !important
        align-self: center
        padding: 0 8px 0 0 !important

    ::v-deep .v-icon
        color: $dark-new
        font-size: 22px

    ::v-deep .v-text-field__details
        display: none

@media (max-width: 980px)
    .crm-grid
        grid-template-columns: repeat(3, minmax(0, 1fr))

@media (max-width: 700px)
    .panel.pad
        padding: 20px
    .section-title
        flex-direction: column
        gap: 12px
    .crm-grid
        grid-template-columns: repeat(2, minmax(0, 1fr))
    .crm-toolbar
        flex-direction: column
        align-items: stretch
    .outline-btn
        width: 100%
    .timezone-card
        padding: 24px

        h2
            font-size: 26px
</style>
